(() => {
  const initializedLandings = new WeakSet();
  const publishedPurchaseStates = new WeakMap();
  let submitHandlerAttached = false;
  let purchaseInProgress = false;

  const expectedProtectionCents = Object.freeze({
    1: 2499,
    3: 7497,
    5: 12495,
  });

  function emit(name, detail = {}) {
    document.dispatchEvent(
      new CustomEvent(name, {
        detail: { ...detail, path: window.location.pathname },
      })
    );
  }

  function escapeSelector(value) {
    if (window.CSS?.escape) return window.CSS.escape(value);
    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  function closestProductForm(element, landing) {
    const directScope = element.closest('[data-fs-ad-product-form]');
    if (directScope) return directScope;

    const panel = element.closest('[data-fs-ad-panel]');
    if (panel) {
      const panelScope = panel.querySelector('[data-fs-ad-product-form]');
      if (panelScope) return panelScope;
    }

    return landing.querySelector('[data-fs-ad-product-form]');
  }

  function offerScope(offer, productForm, landing) {
    return (
      offer.closest('[data-fs-ad-offers]') ||
      offer.closest('[data-fs-ad-panel]') ||
      productForm ||
      landing
    );
  }

  function getOfferVariantId(offer) {
    return (
      offer.dataset.fsAdVariant ||
      offer.dataset.fsAdVariantId ||
      offer.dataset.variantId ||
      offer.getAttribute('data-variant-id') ||
      offer.dataset.fsAdOffer ||
      ''
    );
  }

  function protectionMap(productForm) {
    const mappingNode = productForm.querySelector('[data-fs-ad-protection-variants]');
    if (!mappingNode) return {};

    try {
      return JSON.parse(mappingNode.textContent || '{}');
    } catch (_) {
      return {};
    }
  }

  function selectedOffer(productForm) {
    return (
      productForm?.querySelector('[data-fs-ad-offer-selected]') ||
      productForm?.querySelector('[data-fs-ad-offer]') ||
      null
    );
  }

  function mappedProtection(productForm, productVariantId, units) {
    const mapping = protectionMap(productForm)[String(productVariantId || '')] || {};
    const id = Number.parseInt(mapping.id || '', 10);
    const priceCents = Number.parseInt(mapping.priceCents ?? '', 10);
    const mappedUnits = Number.parseInt(mapping.units || '', 10);
    const expectedCents = productForm?.dataset.fsFreeProtection === 'true' && [1, 3, 5].includes(units) ? 0 : expectedProtectionCents[units];
    const catalogValid = productForm?.dataset.fsAdProtectionCatalogValid === 'true';
    const valid = Boolean(
      catalogValid &&
        Number.isInteger(id) &&
        id > 0 &&
        Number.isInteger(expectedCents) &&
        Number.isInteger(priceCents) &&
        priceCents === expectedCents &&
        mappedUnits === units
    );

    return {
      ...mapping,
      id,
      priceCents,
      units: mappedUnits,
      valid,
    };
  }

  function formatMoney(productForm, cents) {
    const landing = productForm.closest('[data-fs-ad-landing]');
    const currency = landing?.dataset.fsCurrency || window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
      }).format(Math.max(0, Number(cents) || 0) / 100);
    } catch (_) {
      return `$${(Math.max(0, Number(cents) || 0) / 100).toFixed(2)}`;
    }
  }

  function publishPurchaseState(productForm, state, reason) {
    const landing = productForm.closest('[data-fs-ad-landing]');
    if (!landing) return false;

    const signature = [
      state.model,
      state.variantId,
      state.units,
      state.protectionSelected,
      state.protectionVariantId,
      state.totalCents,
      state.mappingValid,
    ].join('|');
    if (publishedPurchaseStates.get(landing) === signature) return false;

    publishedPurchaseStates.set(landing, signature);
    document.dispatchEvent(
      new CustomEvent('fieldsignal:purchase_state_changed', {
        detail: {
          productForm,
          reason,
          model: state.model,
          variantId: state.variantId,
          units: state.units,
          protectionSelected: state.protectionSelected,
          protectionVariantId: state.protectionVariantId,
          protectionCents: state.protectionCents,
          totalCents: state.totalCents,
          mappingValid: state.mappingValid,
          path: window.location.pathname,
        },
      })
    );
    return true;
  }

  function syncOrderTotal(productForm, options = {}) {
    if (!productForm) return null;

    const offer = selectedOffer(productForm);
    const planChoice = productForm.querySelector('[data-fs-ad-protection-choice="plan"]');
    const variantInput = productForm.querySelector('input[data-fs-ad-variant-input]');
    const variantId = String(variantInput?.value || getOfferVariantId(offer) || '');
    const units = Math.max(1, Number.parseInt(offer?.dataset.fsAdUnits || '1', 10) || 1);
    const hardwareCents = Math.max(0, Number(offer?.dataset.fsAdPriceCents || 0));
    if (productForm.dataset.fsFreeProtection === 'true' && planChoice) planChoice.checked = true;
    const protection = mappedProtection(productForm, variantId, units);
    const protectionSelected = Boolean(
      protection.valid && planChoice?.checked && !planChoice.disabled
    );
    const protectionCents = protectionSelected
      ? protection.priceCents
      : 0;
    const total = hardwareCents + protectionCents;
    const model = productForm.closest('[data-fs-ad-version]')?.dataset.fsAdVersion || '';
    const modelLabel = model === 'pro' ? 'Pro' : 'Standard';
    const pairLabel = `${units} ${units === 1 ? 'pair' : 'pairs'}`;
    const summary = protectionSelected
      ? `${pairLabel} + ${productForm.dataset.fsFreeProtection === 'true' ? 'FREE ' : ''}lifetime protection`
      : `${pairLabel} · hardware only`;

    productForm.dataset.fsOrderTotalCents = String(total);
    productForm.dataset.fsProtectionMappingValid = String(protection.valid);
    productForm.querySelectorAll('[data-fs-ad-order-total]').forEach((node) => {
      node.textContent = formatMoney(productForm, total);
    });
    productForm.querySelectorAll('[data-fs-ad-order-summary]').forEach((node) => {
      node.textContent = summary;
    });
    productForm.querySelectorAll('[data-fs-ad-submit-detail]').forEach((node) => {
      node.textContent = protection.valid
        ? `${pairLabel} ${modelLabel}${protectionSelected ? (productForm.dataset.fsFreeProtection === 'true' ? ' + FREE lifetime protection' : ' + lifetime protection') : ''} · ${formatMoney(productForm, total)}`
        : 'Lifetime protection setup unavailable';
    });
    productForm.querySelectorAll('[data-fs-ad-protection-coverage]').forEach((node) => {
      node.textContent = `${pairLabel} covered`;
    });
    productForm.querySelectorAll('[data-fs-ad-protection-state]').forEach((node) => {
      node.textContent = protectionSelected ? 'Selected' : 'Removed';
    });

    const unitsProperty = productForm.querySelector('[data-fs-ad-units-property]');
    const offerProperty = productForm.querySelector('[data-fs-ad-offer-property]');
    const protectionProperty = productForm.querySelector('[data-fs-ad-protection-property]');
    if (unitsProperty) unitsProperty.value = String(units);
    if (offerProperty) offerProperty.value = pairLabel;
    if (protectionProperty) protectionProperty.value = protectionSelected ? 'true' : 'false';

    const submitButton = productForm.querySelector('[type="submit"]');
    if (submitButton && productForm.getAttribute('aria-busy') !== 'true') {
      submitButton.disabled = !protection.valid;
      submitButton.toggleAttribute('aria-disabled', !protection.valid);
    }

    const state = {
      productForm,
      model,
      variantId,
      units,
      hardwareCents,
      protectionCents,
      protectionSelected,
      protectionVariantId: protectionSelected ? String(protection.id) : '',
      totalCents: total,
      mappingValid: protection.valid,
    };

    if (options.announce) {
      publishPurchaseState(productForm, state, options.reason || 'update');
    }

    return state;
  }

  function syncProtectionChoice(choice) {
    const protection = choice?.closest('[data-fs-ad-protection]');
    if (!protection) return;

    protection.querySelectorAll('[data-fs-ad-protection-card]').forEach((card) => {
      card.toggleAttribute(
        'data-fs-ad-protection-selected',
        Boolean(card.querySelector('[data-fs-ad-protection-choice]:checked'))
      );
    });

    const productForm = protection.closest('[data-fs-ad-product-form]');
    const planSelected =
      choice.matches('[data-fs-ad-protection-choice="plan"]') &&
      choice.checked &&
      !choice.disabled;
    productForm?.toggleAttribute('data-fs-ad-protection-selected', planSelected);
  }

  function setProtectionPlanForVariant(productForm, productVariantId) {
    const planChoice = productForm.querySelector('[data-fs-ad-protection-choice="plan"]');
    if (!planChoice) {
      productForm.dataset.fsProtectionMappingValid = 'false';
      return false;
    }

    const offer = selectedOffer(productForm);
    const units = Math.max(1, Number.parseInt(offer?.dataset.fsAdUnits || '1', 10) || 1);
    const protection = mappedProtection(productForm, productVariantId, units);
    const choiceCard = planChoice.closest('[data-fs-ad-protection-card]');

    if (!protection.valid) {
      planChoice.disabled = true;
      planChoice.checked = false;
      delete planChoice.dataset.fsAdProtectionVariant;
      delete planChoice.dataset.fsAdProtectionPriceCents;
      delete planChoice.dataset.fsAdProtectionUnits;
      choiceCard?.setAttribute('data-fs-ad-protection-unavailable', 'true');
      syncProtectionChoice(planChoice);
      productForm.dataset.fsProtectionMappingValid = 'false';
      return false;
    }

    planChoice.disabled = false;
    planChoice.dataset.fsAdProtectionVariant = String(protection.id);
    planChoice.dataset.fsAdProtectionTitle =
      protection.title || 'Field Signal Lifetime Protection Plan';
    planChoice.dataset.fsAdProtectionPriceCents = String(protection.priceCents || 0);
    planChoice.dataset.fsAdProtectionUnits = String(protection.units || 1);
    choiceCard?.removeAttribute('data-fs-ad-protection-unavailable');

    productForm.querySelectorAll('[data-fs-ad-protection-price]').forEach((price) => {
      price.textContent = productForm.dataset.fsFreeProtection === 'true' ? 'FREE' : (protection.price || '');
    });
    productForm.dataset.fsProtectionMappingValid = 'true';
    return true;
  }

  function syncPdpMetrics(landing, selectedTab) {
    if (!landing || !selectedTab) return;

    const facts = {
      response: {
        value: selectedTab.dataset.fsPdpResponse || '',
        copy: selectedTab.dataset.fsPdpResponseCopy || '',
      },
      protection: {
        value: selectedTab.dataset.fsPdpProtection || '',
        copy: selectedTab.dataset.fsPdpProtectionCopy || '',
      },
      nrr: {
        value: selectedTab.dataset.fsPdpNrr || '',
        copy: selectedTab.dataset.fsPdpNrrCopy || '',
      },
      battery: {
        value: selectedTab.dataset.fsPdpBattery || '',
        copy: selectedTab.dataset.fsPdpBatteryCopy || '',
      },
    };

    Object.entries(facts).forEach(([name, fact]) => {
      landing.querySelectorAll(`[data-fs-pdp-metric-value="${name}"]`).forEach((node) => {
        node.textContent = fact.value;
      });
      landing.querySelectorAll(`[data-fs-pdp-metric-copy="${name}"]`).forEach((node) => {
        node.textContent = fact.copy;
      });
    });

    const hardware = landing.querySelector('.fs-pdp-hardware');
    const hardwareImage = hardware?.querySelector('.fs-pdp-hardware__grid > figure img');
    const hardwareSource = versionImageSource(hardware, selectedTab.dataset.fsAdVersion);
    if (hardwareImage && hardwareSource) {
      hardwareImage.src = hardwareSource;
      hardwareImage.alt = selectedTab.dataset.fsPdpAlt || hardwareImage.alt;
    }
    const isStandard = selectedTab.dataset.fsAdVersion === 'standard';
    const modelName = hardware?.querySelector('[data-fs-pdp-model-name]');
    const modelCopy = hardware?.querySelector('[data-fs-pdp-model-copy]');
    if (modelName) modelName.textContent = isStandard ? 'Field Signal Standard' : 'Field Signal Pro';
    if (modelCopy) modelCopy.textContent = isStandard
      ? '27 dB hearing protection.'
      : '31 dB hearing protection with an LED charging-case display.';
  }

  function versionImageSource(hardware, version) {
    if (!hardware) return '';
    return version === 'standard'
      ? hardware.dataset.fsPdpHardwareStandard || ''
      : hardware.dataset.fsPdpHardwarePro || '';
  }

  function setGalleryImage(gallery, index, shouldScroll = true) {
    const lead = gallery?.querySelector('[data-fs-pdp-gallery-lead]');
    const thumbs = Array.from(gallery?.querySelectorAll('[data-fs-pdp-gallery-thumb]') || []);
    if (!lead || !thumbs.length) return;

    const normalizedIndex = (index + thumbs.length) % thumbs.length;
    const selectedThumb = thumbs[normalizedIndex];
    const source = selectedThumb.dataset.fsPdpGallerySrc || '';
    const alt = selectedThumb.dataset.fsPdpGalleryAlt || lead.alt || '';

    if (source) lead.src = source;
    lead.alt = alt;

    const zoomImage = gallery.querySelector('[data-fs-pdp-gallery-zoom-image]');
    if (zoomImage) {
      zoomImage.src = source;
      zoomImage.alt = alt;
    }

    thumbs.forEach((thumb, thumbIndex) => {
      const selected = thumbIndex === normalizedIndex;
      thumb.classList.toggle('is-selected', selected);
      thumb.setAttribute('aria-pressed', String(selected));
    });

    const status = gallery.querySelector('[data-fs-pdp-gallery-status]');
    if (status) status.textContent = `${normalizedIndex + 1} / ${thumbs.length}`;

    if (shouldScroll) {
      selectedThumb.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }

  function initializeGallery(landing) {
    landing.querySelectorAll('[data-fs-ad-gallery-panel]').forEach((gallery) => {
      const thumbs = Array.from(gallery.querySelectorAll('[data-fs-pdp-gallery-thumb]'));
      const selectedIndex = Math.max(
        0,
        thumbs.findIndex((thumb) => thumb.classList.contains('is-selected'))
      );
      setGalleryImage(gallery, selectedIndex, false);
    });
  }

  function getStatus(productForm, landing) {
    const status =
      productForm?.querySelector('[data-fs-ad-status]') ||
      productForm?.closest('[data-fs-ad-panel]')?.querySelector('[data-fs-ad-status]') ||
      landing.querySelector('[data-fs-ad-status]');

    if (status) {
      status.setAttribute('aria-live', 'polite');
    }

    return status;
  }

  function setStatus(status, message, isError = false) {
    if (!status) return;

    status.hidden = false;
    status.textContent = message;
    status.setAttribute('role', isError ? 'alert' : 'status');
  }

  function lineItemProperties(productForm) {
    const properties = {};
    const formData = new FormData(productForm);

    formData.forEach((value, key) => {
      const match = /^properties\[([^\]]+)\]$/.exec(key);
      const normalizedValue = typeof value === 'string' ? value.trim() : '';
      if (!match || !normalizedValue) return;
      properties[match[1]] = normalizedValue;
    });

    return properties;
  }

  function setSelectedOffer(offer, landing) {
    const productForm = closestProductForm(offer, landing);
    if (!productForm) return;

    const variantInput = productForm.querySelector('input[data-fs-ad-variant-input]');
    const variantId = getOfferVariantId(offer);
    const scope = offerScope(offer, productForm, landing);

    scope.querySelectorAll('[data-fs-ad-offer]').forEach((candidate) => {
      const selected = candidate === offer;
      candidate.setAttribute('aria-pressed', String(selected));
      candidate.toggleAttribute('data-fs-ad-offer-selected', selected);
      candidate.classList.toggle('is-selected', selected);
    });

    if (variantInput && variantId) {
      variantInput.value = variantId;
      variantInput.dispatchEvent(new Event('input', { bubbles: true }));
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      setProtectionPlanForVariant(productForm, variantId);
    }

    const unitsProperty = productForm.querySelector('[data-fs-ad-units-property]');
    const offerProperty = productForm.querySelector('[data-fs-ad-offer-property]');
    if (unitsProperty) unitsProperty.value = offer.dataset.fsAdUnits || '1';
    if (offerProperty) offerProperty.value = offer.dataset.fsAdOfferLabel || '';

    const price = offer.dataset.fsAdPrice || offer.getAttribute('data-price') || '';
    const compare = offer.dataset.fsAdCompare || offer.getAttribute('data-compare') || '';
    const displayScope = productForm.closest('[data-fs-ad-panel]') || productForm;

    displayScope.querySelectorAll('[data-fs-ad-selected-price]').forEach((node) => {
      node.textContent = price;
    });

    displayScope.querySelectorAll('[data-fs-ad-selected-compare]').forEach((node) => {
      node.textContent = compare;
      node.hidden = !compare;
    });

    syncOrderTotal(productForm, { announce: true, reason: 'offer' });

    emit('fieldsignal:bundle_select', {
      variantId,
      label: offer.dataset.fsAdOfferLabel || offer.textContent.trim(),
      price,
      compare,
    });
  }

  function activateVersion(landing, selectedTab, moveFocus = false, announceState = true) {
    const tabs = Array.from(
      landing.querySelectorAll('[role="tab"][data-fs-ad-version]')
    );
    if (!tabs.length || !selectedTab) return;

    const version = selectedTab.dataset.fsAdVersion || '';
    const controlledPanelId = selectedTab.getAttribute('aria-controls');
    const controlledPanel = controlledPanelId ? document.getElementById(controlledPanelId) : null;

    tabs.forEach((tab) => {
      const selected = tab === selectedTab;
      tab.setAttribute('aria-selected', String(selected));
      tab.setAttribute('tabindex', selected ? '0' : '-1');
      tab.toggleAttribute('data-fs-ad-version-selected', selected);
    });

    const panels = Array.from(landing.querySelectorAll('[data-fs-ad-panel]'));
    const matchingPanels = panels.filter((panel) => {
      if (controlledPanel && panel === controlledPanel) return true;
      return (
        panel.dataset.fsAdPanel === version ||
        panel.dataset.fsAdVersion === version
      );
    });

    if (matchingPanels.length) {
      panels.forEach((panel) => {
        const selected = matchingPanels.includes(panel);
        panel.hidden = !selected;
        panel.setAttribute('aria-hidden', String(!selected));
      });
    }

    const heading = landing.querySelector('[data-fs-pdp-heading]');
    if (heading) {
      heading.textContent = selectedTab.dataset.fsPdpTitle || '';
    }

    syncPdpMetrics(landing, selectedTab);
    emit('fieldsignal:version_change', { version });

    if (announceState) {
      const activeForm = matchingPanels
        .map((panel) => panel.querySelector('[data-fs-ad-product-form]'))
        .find(Boolean);
      syncOrderTotal(activeForm, { announce: true, reason: 'version' });
    }

    if (moveFocus) selectedTab.focus();
  }

  function focusScrollTarget(target) {
    if (!target) return;

    target.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'center',
    });

    window.setTimeout(() => {
      const focusTarget =
        target.matches('input, select, textarea, button, a[href]')
          ? target
          : target.querySelector(
              'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), a[href]'
            ) || target;

      if (!focusTarget.hasAttribute('tabindex') && focusTarget === target) {
        focusTarget.setAttribute('tabindex', '-1');
      }
      if (typeof focusTarget.focus === 'function') {
        focusTarget.focus({ preventScroll: true });
      }
    }, 450);
  }

  function resolveScrollTarget(trigger, landing) {
    const targetReference = trigger.dataset.fsAdScrollToOffer || '';
    if (targetReference) {
      try {
        const target = landing.querySelector(targetReference);
        if (target) return target;
      } catch (_) {
        // Treat non-selector values as an id or an offer target name below.
      }

      const id = targetReference.replace(/^#/, '');
      const byId = document.getElementById(id);
      if (byId && landing.contains(byId)) return byId;

      const namedTarget = landing.querySelector(
        `[data-fs-ad-offer-target="${escapeSelector(targetReference)}"]`
      );
      if (namedTarget) return namedTarget;
    }

    return landing.querySelector('[data-fs-ad-product-form]') || landing.querySelector('[data-fs-ad-offer]');
  }

  function handleLandingClick(event, landing) {
    const zoomOpen = event.target.closest('[data-fs-pdp-gallery-zoom-open]');
    if (zoomOpen && landing.contains(zoomOpen)) {
      event.preventDefault();
      const dialog = zoomOpen.closest('[data-fs-ad-gallery-panel]')?.querySelector('[data-fs-pdp-gallery-zoom]');
      if (dialog?.showModal) dialog.showModal();
      return;
    }

    const zoomClose = event.target.closest('[data-fs-pdp-gallery-zoom-close]');
    if (zoomClose && landing.contains(zoomClose)) {
      event.preventDefault();
      zoomClose.closest('[data-fs-pdp-gallery-zoom]')?.close();
      return;
    }

    const quickControl = event.target.closest('[data-fs-pdp-quick-prev], [data-fs-pdp-quick-next]');
    if (quickControl && landing.contains(quickControl)) {
      event.preventDefault();
      const nav = quickControl.closest('.fs-pdp-quick-links__rail')?.querySelector('[data-fs-pdp-quick-nav]');
      const direction = quickControl.matches('[data-fs-pdp-quick-next]') ? 1 : -1;
      nav?.scrollBy({ left: direction * Math.max(180, nav.clientWidth * 0.72), behavior: 'smooth' });
      return;
    }

    const galleryControl = event.target.closest('[data-fs-pdp-gallery-prev], [data-fs-pdp-gallery-next]');
    if (galleryControl && landing.contains(galleryControl)) {
      event.preventDefault();
      const gallery = galleryControl.closest('[data-fs-ad-gallery-panel]');
      const thumbs = Array.from(gallery?.querySelectorAll('[data-fs-pdp-gallery-thumb]') || []);
      const selectedIndex = thumbs.findIndex((thumb) => thumb.classList.contains('is-selected'));
      const direction = galleryControl.matches('[data-fs-pdp-gallery-next]') ? 1 : -1;
      setGalleryImage(gallery, selectedIndex + direction);
      return;
    }

    const galleryThumb = event.target.closest('[data-fs-pdp-gallery-thumb]');
    if (galleryThumb && landing.contains(galleryThumb)) {
      event.preventDefault();
      const gallery = galleryThumb.closest('[data-fs-ad-gallery-panel]');
      const thumbs = Array.from(gallery?.querySelectorAll('[data-fs-pdp-gallery-thumb]') || []);
      setGalleryImage(gallery, thumbs.indexOf(galleryThumb));
      return;
    }

    const versionTab = event.target.closest('[role="tab"][data-fs-ad-version]');
    if (versionTab && landing.contains(versionTab)) {
      event.preventDefault();
      activateVersion(landing, versionTab, true);
      return;
    }

    const offer = event.target.closest('[data-fs-ad-offer]');
    if (offer && landing.contains(offer)) {
      event.preventDefault();
      setSelectedOffer(offer, landing);
      return;
    }

    const scrollTrigger = event.target.closest('[data-fs-ad-scroll-to-offer]');
    if (scrollTrigger && landing.contains(scrollTrigger)) {
      event.preventDefault();
      focusScrollTarget(resolveScrollTarget(scrollTrigger, landing));
    }
  }

  function handleLandingKeydown(event, landing) {
    const galleryThumb = event.target.closest('[data-fs-pdp-gallery-thumb]');
    if (galleryThumb && landing.contains(galleryThumb)) {
      const gallery = galleryThumb.closest('[data-fs-ad-gallery-panel]');
      const thumbs = Array.from(gallery?.querySelectorAll('[data-fs-pdp-gallery-thumb]') || []);
      const currentIndex = thumbs.indexOf(galleryThumb);
      let nextIndex;

      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = currentIndex - 1;
      else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = currentIndex + 1;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = thumbs.length - 1;
      else return;

      event.preventDefault();
      setGalleryImage(gallery, nextIndex);
      thumbs[(nextIndex + thumbs.length) % thumbs.length]?.focus();
      return;
    }

    const tab = event.target.closest('[role="tab"][data-fs-ad-version]');
    if (!tab || !landing.contains(tab)) return;

    const tabs = Array.from(
      landing.querySelectorAll('[role="tab"][data-fs-ad-version]')
    );
    const currentIndex = tabs.indexOf(tab);
    if (currentIndex < 0) return;

    let nextIndex;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = tabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    activateVersion(landing, tabs[nextIndex], true);
  }

  function handleLandingChange(event, landing) {
    const colorChoice = event.target.closest('[data-fs-pdp-color]');
    if (colorChoice && landing.contains(colorChoice) && colorChoice.checked) {
      const productForm = colorChoice.closest('[data-fs-ad-product-form]');
      productForm?.querySelectorAll('[data-fs-pdp-color-name]').forEach((node) => {
        node.textContent = colorChoice.value;
      });
      emit('fieldsignal:variant_select', {
        option: 'Color',
        value: colorChoice.value,
      });
      return;
    }

    const choice = event.target.closest('[data-fs-ad-protection-choice]');
    if (!choice || !landing.contains(choice)) return;

    syncProtectionChoice(choice);
    syncOrderTotal(choice.closest('[data-fs-ad-product-form]'), {
      announce: true,
      reason: 'protection',
    });
    emit('fieldsignal:protection_plan_select', {
      selected:
        choice.matches('[data-fs-ad-protection-choice="plan"]') &&
        choice.checked &&
        !choice.disabled,
      variantId: choice.dataset.fsAdProtectionVariant || '',
      label: choice.dataset.fsAdProtectionTitle || '',
    });
  }

  function resetSubmitState(productForm) {
    productForm.removeAttribute('aria-busy');
    const submitButton = productForm.querySelector('[type="submit"]');
    if (submitButton) {
      const mappingValid = productForm.dataset.fsProtectionMappingValid === 'true';
      submitButton.disabled = !mappingValid;
      submitButton.toggleAttribute('aria-disabled', !mappingValid);
      submitButton.removeAttribute('aria-label');
    }
  }

  async function submitToCheckout(event, productForm, landing) {
    if (purchaseInProgress || productForm.getAttribute('aria-busy') === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (productForm.dataset.fsFreeProtection === 'true' && Date.now() >= Number(productForm.dataset.fsTestEnd) * 1000) {
      event.preventDefault();
      event.stopImmediatePropagation();
      setStatus(getStatus(productForm, landing), 'This offer has ended. Refresh the page to review the current offer before ordering.', true);
      return;
    }
    const purchaseState = syncOrderTotal(productForm);
    const productVariantId = Number.parseInt(purchaseState?.variantId || '', 10);
    const planChoice = purchaseState?.protectionSelected
      ? productForm.querySelector('[data-fs-ad-protection-choice="plan"]:checked:not(:disabled)')
      : null;
    const protectionVariantId = purchaseState?.protectionSelected
      ? Number.parseInt(purchaseState.protectionVariantId || '', 10)
      : null;
    const status = getStatus(productForm, landing);

    if (
      !productVariantId ||
      !purchaseState?.mappingValid ||
      (planChoice && !protectionVariantId)
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();
      setStatus(
        status,
        'Lifetime protection is temporarily unavailable. Please refresh or contact us before ordering.',
        true
      );
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const submitButton =
      event.submitter || productForm.querySelector('[type="submit"]');
    const quantity = Math.max(
      1,
      Number.parseInt(productForm.querySelector('[name="quantity"]')?.value || '1', 10) || 1
    );
    const selectedOffer = productForm.querySelector('[data-fs-ad-offer-selected]');
    const model = purchaseState.model;
    const protectedUnits = purchaseState.units;
    const attributionApi = window.FieldSignalAttribution;
    const addEventId = attributionApi?.createEventId?.('AddToCart') || '';
    const checkoutEventId = attributionApi?.createEventId?.('InitiateCheckout') || '';
    const currency = window.Shopify?.currency?.active || 'USD';
    const orderTotalCents = purchaseState.totalCents;
    const selectionMetadata = {
      fs_protection_test: productForm.dataset.fsFreeProtection === 'true' ? 'free-48h-2026-09-25' : 'paid-control',
      fs_model: model,
      fs_quantity: String(protectedUnits),
      fs_offer_selection: selectedOffer?.dataset?.fsAdOfferLabel || '',
      fs_variant_id: String(productVariantId),
      fs_protection_variant_id: protectionVariantId ? String(protectionVariantId) : '',
      fs_protection_quantity: protectionVariantId ? String(protectedUnits) : '0',
      fs_protection_selected: protectionVariantId ? 'true' : 'false',
      fs_order_total_cents: String(orderTotalCents),
      fs_currency: currency,
      fs_add_event_id: addEventId,
      fs_checkout_event_id: checkoutEventId,
    };

    purchaseInProgress = true;
    productForm.setAttribute('aria-busy', 'true');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.setAttribute('aria-label', 'Adding your order to cart');
    }

    emit('fieldsignal:add_to_cart_start', {
      productVariantId: String(productVariantId),
      protectionVariantId: protectionVariantId ? String(protectionVariantId) : '',
    });

    try {
      const rootUrl = window.Shopify?.routes?.root || '/';
      const root = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
      const cleared = await fetch(`${root}cart/clear.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!cleared.ok) throw new Error('Your cart could not be reset. Please try again.');
      const clearedCart = await cleared.json();
      if (clearedCart.item_count !== 0) throw new Error('Your cart could not be reset. Please try again.');

      await attributionApi?.syncCartAttributes?.(selectionMetadata, { force: true });

      const routes = typeof Theme !== 'undefined' && Theme.routes ? Theme.routes : {};
      const productItem = { id: productVariantId, quantity };
      const protectionLinkId = protectionVariantId
        ? window.crypto?.randomUUID?.() || `fs-protection-${Date.now()}-${Math.random().toString(16).slice(2)}`
        : '';
      const properties = {
        ...lineItemProperties(productForm),
        ...attributionApi?.getLineItemProperties?.(selectionMetadata),
        _fs_line_role: 'hardware',
      };
      if (protectionLinkId) properties._fs_protection_link = protectionLinkId;
      if (Object.keys(properties).length) productItem.properties = properties;
      const items = [productItem];

      if (protectionVariantId) {
        items.push({
          id: protectionVariantId,
          quantity,
          parent_id: productVariantId,
          properties: {
            ...attributionApi?.getLineItemProperties?.(selectionMetadata),
            _fs_line_role: 'protection',
            _fs_parent: protectionLinkId,
            _fs_parent_variant: String(productVariantId),
            _fs_covered_pairs: String(protectedUnits),
          },
        });
      }

      const response = await fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || error.message || 'Your cart could not be updated.');
      }

      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!cartResponse.ok) throw new Error('We could not verify your cart. Please try again.');
      const cart = await cartResponse.json();
      const expected = protectionVariantId ? 2 : 1;
      const hardware = cart.items?.filter((item) => item.id === productVariantId && item.quantity === quantity && item.properties?._fs_line_role === 'hardware') || [];
      const protection = cart.items?.filter((item) => item.id === protectionVariantId && item.quantity === quantity && item.properties?._fs_line_role === 'protection' && item.properties?._fs_parent === protectionLinkId) || [];
      if (cart.items?.length !== expected || hardware.length !== 1 || (protectionVariantId && protection.length !== 1)) {
        throw new Error('We could not verify your cart. Please try again.');
      }

      attributionApi?.trackCommerce?.(
        'AddToCart',
        {
          model,
          quantity: protectedUnits,
          product_variant_id: String(productVariantId),
          protection_selected: Boolean(protectionVariantId),
          protection_variant_id: protectionVariantId ? String(protectionVariantId) : '',
          value: orderTotalCents / 100,
          currency,
        },
        addEventId || undefined
      );

      emit('fieldsignal:checkout_start', {
        productVariantId: String(productVariantId),
        protectionVariantId: protectionVariantId ? String(protectionVariantId) : '',
      });
      attributionApi?.trackCommerce?.(
        'InitiateCheckout',
        {
          model,
          quantity: protectedUnits,
          product_variant_id: String(productVariantId),
          protection_selected: Boolean(protectionVariantId),
          protection_variant_id: protectionVariantId ? String(protectionVariantId) : '',
          value: orderTotalCents / 100,
          currency,
        },
        checkoutEventId || undefined
      );

      const checkoutUrl = `${root}checkout`;
      window.location.assign(checkoutUrl);
    } catch (error) {
      purchaseInProgress = false;
      attributionApi?.trackDiagnostic?.('add_fail', {
        ...selectionMetadata,
        error: String(error?.message || 'Unknown cart error').slice(0, 160),
      });
      setStatus(status, error?.message || 'Your cart could not be updated. Please try again.', true);
      productForm.removeAttribute('aria-busy');
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-label');
      }
    }
  }

  function attachSubmitHandler() {
    if (submitHandlerAttached) return;
    submitHandlerAttached = true;

    // Horizon handles product submits from document capture. Window capture lets
    // this landing page add the exact selection before opening checkout.
    window.addEventListener(
      'submit',
      (event) => {
        const form = event.target instanceof Element ? event.target.closest('form') : null;
        const productForm =
          form?.matches('[data-fs-ad-product-form]')
            ? form
            : form?.closest('[data-fs-ad-product-form]');
        const landing = productForm?.closest('[data-fs-ad-landing]');
        if (!form || !productForm || !landing) return;
        submitToCheckout(event, productForm, landing);
      },
      true
    );
  }

  function initializeMidnightOfferTimer(landing) {
    const timer = landing.querySelector('[data-fs-pdp-sale-timer]');
    const countdown = timer?.querySelector('[data-fs-pdp-sale-countdown]');
    if (!timer || !countdown || timer.dataset.fsPdpSaleTimerInitialized === 'true') return;

    timer.dataset.fsPdpSaleTimerInitialized = 'true';

    const pad = (value) => String(value).padStart(2, '0');
    const nextLocalMidnight = () => {
      const target = new Date();
      target.setHours(24, 0, 0, 0);
      return target;
    };

    let target = nextLocalMidnight();

    const update = () => {
      let remaining = target.getTime() - Date.now();
      if (remaining <= 0) {
        target = nextLocalMidnight();
        remaining = target.getTime() - Date.now();
      }

      const totalSeconds = Math.max(0, Math.floor(remaining / 1000));
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      const label = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;

      countdown.textContent = label;
      timer.setAttribute(
        'aria-label',
        `Black Friday Early Access countdown: ${hours} hours, ${minutes} minutes, and ${seconds} seconds remaining`
      );
    };

    update();
    window.setInterval(update, 1000);
  }

  function initializeLanding(landing) {
    if (initializedLandings.has(landing)) return;
    initializedLandings.add(landing);
    initializeMidnightOfferTimer(landing);

    const tabs = Array.from(
      landing.querySelectorAll('[role="tab"][data-fs-ad-version]')
    );

    landing.querySelectorAll('[data-fs-ad-panel]:not([data-fs-ad-gallery-panel])').forEach((panel) => {
      if (!panel.hasAttribute('role')) panel.setAttribute('role', 'tabpanel');
    });

    const requestedVersion = new URLSearchParams(window.location.search)
      .get('version')
      ?.trim()
      .toLowerCase();
    const requestedTab = requestedVersion
      ? tabs.find(
          (tab) => (tab.dataset.fsAdVersion || '').trim().toLowerCase() === requestedVersion
        )
      : null;
    const selectedTab =
      requestedTab || tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
    if (selectedTab) activateVersion(landing, selectedTab, false, false);
    initializeGallery(landing);

    landing.querySelectorAll('[data-fs-ad-product-form]').forEach((productForm) => {
      const variantId = productForm.querySelector('[data-fs-ad-variant-input]')?.value;
      setProtectionPlanForVariant(productForm, variantId);

      const selectedChoice = productForm.querySelector(
        '[data-fs-ad-protection-choice]:checked'
      );
      if (selectedChoice) syncProtectionChoice(selectedChoice);
      syncOrderTotal(productForm);
      resetSubmitState(productForm);
    });

    const activeForm = landing.querySelector('[data-fs-ad-panel]:not([hidden]) [data-fs-ad-product-form]');
    syncOrderTotal(activeForm, { announce: true, reason: 'initialization' });
    const activeProtection = activeForm?.querySelector('[data-fs-ad-protection-choice="plan"]');
    if (activeProtection) {
      emit('fieldsignal:protection_plan_impression', {
        selected: activeProtection.checked,
        variantId: activeProtection.dataset.fsAdProtectionVariant || '',
        label: activeProtection.dataset.fsAdProtectionTitle || '',
      });
    }

    landing.addEventListener('click', (event) => handleLandingClick(event, landing));
    landing.addEventListener('keydown', (event) => handleLandingKeydown(event, landing));
    landing.addEventListener('change', (event) => handleLandingChange(event, landing));
  }

  function initialize() {
    document.querySelectorAll('[data-fs-ad-landing]').forEach(initializeLanding);
    attachSubmitHandler();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  // Browsers can restore the landing page from the back-forward cache with its
  // pre-navigation DOM state intact. Always restore the CTA when the page returns.
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('[data-fs-ad-product-form]').forEach(resetSubmitState);
  });
})();
